import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HUMAN_FRONT, HUMAN_BACK } from './geometry/humanSilhouette';
import { labelFor } from './bodyAreaLabels';
import styles from './demos.module.css';

const VIEWS = { front: HUMAN_FRONT, back: HUMAN_BACK };

/**
 * The body-area picker, as a laser clinic uses it: areas accumulate into a
 * session, because that is how the treatment is scoped and priced.
 *
 * `dir="ltr"` for the same reason as the tooth chart and the face map. The
 * figure is anatomy, and the artwork already encodes which side is whose —
 * flipping it under he/ar would move a patient's left arm onto their right.
 */
const BodyAreaDemo = ({ color = '#10b981', initialPicked = ['abdomen', 'pelvis'] }) => {
  const { t } = useTranslation();
  const [view, setView] = useState('front');

  /**
   * The label for a part, translated. `labelFor` supplies the English as the
   * default value, so a key we have not translated yet degrades to English
   * rather than rendering a raw part name like `leftUpperLeg`.
   */
  const label = (v, key) => t(`demos.bodyLabels.${v}.${key}`, labelFor(v, key));
  const [picked, setPicked] = useState(() => new Set(initialPicked));

  const drawing = VIEWS[view];
  const parts = Object.keys(drawing.parts);

  const toggle = (key) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const count = picked.size;
  const names = [...picked].map((k) => label(view, k));

  return (
    <div className={styles.demoFrame} data-demo="treatment-areas">
      <div className={styles.bodyLayout}>
        <div className={styles.bodyStage}>
          <div className={styles.bodyToggle} role="group" aria-label={t('demos.body.sideAria')}>
            {['front', 'back'].map((v) => (
              <button
                key={v}
                type="button"
                className={`${styles.bodyToggleBtn} ${view === v ? styles.bodyToggleOn : ''}`}
                style={view === v ? { background: color, borderColor: color } : { borderColor: `${color}66`, color }}
                aria-pressed={view === v}
                onClick={() => setView(v)}
              >
                {v === 'front' ? t('demos.body.showFront') : t('demos.body.showBack')}
              </button>
            ))}
          </div>

          <svg
            dir="ltr"
            className={styles.bodySvg}
            viewBox={drawing.viewBox}
            role="group"
            aria-label={t('demos.body.mapAria', { view: view === 'front' ? t('demos.body.frontView') : t('demos.body.backView') })}
          >
            {(drawing.cues || []).map((d, i) => (
              <path
                key={`cue-${i}`}
                d={d}
                fill="none"
                stroke="#cbd5e1"
                strokeWidth={drawing.strokeWidth}
                pointerEvents="none"
              />
            ))}
            {parts.map((key) => {
              const on = picked.has(key);
              return (
                <path
                  key={key}
                  className={styles.bodyPart}
                  d={drawing.parts[key]}
                  fill={on ? `${color}88` : '#eef2f6'}
                  stroke={on ? color : '#cbd5e1'}
                  strokeWidth={drawing.strokeWidth}
                  role="button"
                  tabIndex={0}
                  aria-label={t('demos.body.areaAria', { name: label(view, key) })}
                  aria-pressed={on}
                  onClick={() => toggle(key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(key); }
                  }}
                />
              );
            })}
          </svg>
        </div>

        <div className={styles.bodyReadout} data-testid="body-readout">
          <span className={styles.bodyViewLabel} data-testid="body-view">
            {view === 'front' ? t('demos.body.frontView') : t('demos.body.backView')}
          </span>
          <strong className={styles.bodyCount}>
            {count === 0 ? t('demos.body.none') : t('demos.body.count', { count })}
          </strong>
          <ul className={styles.bodyList}>
            {names.map((n) => (
              <li key={n} style={{ background: `${color}18`, color }}>{n}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BodyAreaDemo;
